import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../components/ui/dialog';
import { MultiSelect } from '../../../components/ui/multi-select';
import { Button } from '../../../components/ui/button';
import {
  useGroupMembers,
  useGpsUsers,
  useAddGroupMember,
  useRemoveGroupMember,
} from '../../../hooks/useValidationGroups';
import { deskListCovers } from '../../../utils/deskAccess';
import type { ValidationGroup } from '../../../api/validationGroupsApi';

interface GroupMembersDialogProps {
  group: ValidationGroup | null;
  onClose: () => void;
}

export const GroupMembersDialog = ({ group, onClose }: GroupMembersDialogProps) => {
  const { t } = useTranslation();
  const groupId = group?.id ?? null;

  const { data: members } = useGroupMembers(groupId);
  const { data: users } = useGpsUsers();
  const add = useAddGroupMember();
  const remove = useRemoveGroupMember();

  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);

  const memberIds = useMemo(() => new Set((members ?? []).map((m) => m.user_id)), [members]);

  // Users of this desk not already members.
  const addableUsers = useMemo(() => {
    if (!group || !users) return [];
    return users.filter((u) => deskListCovers(u.serviceDeskId, group.service_desk_id) && !memberIds.has(u.id));
  }, [group, users, memberIds]);

  // The API adds one member per call; the selection only clears once they all landed,
  // so a failure leaves the picks in place next to the error.
  const handleAdd = () => {
    if (!groupId || !selectedUserIds.length) return;
    Promise.all(selectedUserIds.map((id) => add.mutateAsync({ groupId, userId: Number(id) })))
      .then(() => setSelectedUserIds([]))
      .catch(() => {});
  };

  return (
    <Dialog open={group != null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('Members')} — {group?.name}</DialogTitle>
          <DialogDescription>{t('Members of this group may validate/reject tickets at its gate.')}</DialogDescription>
        </DialogHeader>

        <div className="flex items-end gap-2">
          <div className="flex-1">
            {/* Searchable: the desk lists far too many users to scroll through. The label
                carries the email too, so typing either narrows the list. */}
            <MultiSelect
              options={addableUsers.map((u) => ({ value: String(u.id), label: `${u.username} (${u.email})` }))}
              selected={selectedUserIds}
              onChange={setSelectedUserIds}
              placeholder={t('Add a member')}
              disabled={!addableUsers.length}
              searchable
            />
          </div>
          <Button type="button" onClick={handleAdd} disabled={!selectedUserIds.length || add.isPending}>
            {t('Add')}
          </Button>
        </div>

        <div className="max-h-64 overflow-y-auto divide-y">
          {(members ?? []).length === 0 ? (
            <p className="py-4 text-sm text-gray-500">{t('No member')}</p>
          ) : (
            (members ?? []).map((m) => (
              <div key={m.user_id} className="flex items-center justify-between py-2">
                <div className="text-sm">
                  <span className="font-medium">{m.username}</span>
                  <span className="text-gray-500"> — {m.email}</span>
                </div>
                <button
                  type="button"
                  onClick={() => groupId && remove.mutate({ groupId, userId: m.user_id })}
                  className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                  title={t('Remove')}
                  disabled={remove.isPending}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))
          )}
        </div>

        {(add.error || remove.error) ? (
          <p className="text-sm text-red-600">{((add.error || remove.error) as Error).message}</p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
